import React, { useEffect, useMemo, useState } from "react";
import L from "leaflet";
import { ChevronDown, Satellite } from "lucide-react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Marker,
  Popup,
  ZoomControl,
  useMap,
  useMapEvents
} from "react-leaflet";
import { MILITARY_SHOP_OPTIONS } from "./militaryOptions";

const FIXED_SHOP_COLORS = {
  AGE: "#f59e0b",
  APG: "#2563eb",
  Avionics: "#8b5cf6",
  "E&E": "#14b8a6",
  Engines: "#ef4444",
  "A/R": "#ec4899",
  Hydro: "#0ea5e9",
  "Sheet Metal": "#64748b",
  "Metals Tech": "#78716c",
  Production: "#111827",
  Fuels: "#ea580c",
  Munitions: "#7c2d12",
  MOC: "#4338ca",
  QA: "#b91c1c",
  POL: "#ca8a04",
  Supply: "#0f766e",
  Other: "#6b7280",
  Unassigned: "#94a3b8",
};

const fallbackShopPalette = [
  "#2563eb",
  "#f59e0b",
  "#10b981",
  "#8b5cf6",
  "#ef4444",
  "#14b8a6",
  "#f97316",
  "#ec4899",
  "#84cc16",
  "#0ea5e9",
];

const USER_LOCATION_FRESH_MS = 12 * 60 * 60 * 1000;
const STREET_TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const SATELLITE_TILE_URL = "https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}";

function isFreshUserLocation(person) {
  if (!person?.lastLocationAt) return false;
  const updatedAt = new Date(person.lastLocationAt).getTime();
  return Number.isFinite(updatedAt) && Date.now() - updatedAt <= USER_LOCATION_FRESH_MS;
}

function getStableShopColor(shopName) {
  const shop = String(shopName || "Unassigned").trim() || "Unassigned";
  if (FIXED_SHOP_COLORS[shop]) return FIXED_SHOP_COLORS[shop];

  const normalized = shop.toLowerCase();
  const canonicalShop = MILITARY_SHOP_OPTIONS.find(option => option.toLowerCase() === normalized);
  if (canonicalShop && FIXED_SHOP_COLORS[canonicalShop]) return FIXED_SHOP_COLORS[canonicalShop];

  const hash = [...normalized].reduce((total, character) => total + character.charCodeAt(0), 0);
  return fallbackShopPalette[hash % fallbackShopPalette.length];
}

function MapInvalidateOnShow({ active }) {
  const map = useMap();

  useEffect(() => {
    if (active && map) {
      setTimeout(() => {
        map.invalidateSize({ animate: false });
      }, 0);
    }
  }, [active, map]);

  return null;
}

function MapRecenter({ center, active }) {
  const map = useMap();

  useEffect(() => {
    if (!active || !map || !Array.isArray(center)) return;
    const [lat, lng] = center.map(Number);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    map.setView([lat, lng], map.getZoom(), { animate: false });
    map.invalidateSize({ animate: false });
  }, [active, center, map]);

  return null;
}

function buildShopColorMap(peeps) {
  const uniqueShops = [...new Set(peeps.filter(isFreshUserLocation).map((person) => person.shop || "Unassigned"))].sort();

  return uniqueShops.reduce((map, shop) => {
    map[shop] = getStableShopColor(shop);
    return map;
  }, {});
}

function getAircraftStatusTone(status) {
  const normalized = String(status || "").trim().toUpperCase();
  if (normalized === "CANN") return "cann";
  if (normalized.startsWith("P")) return "p";
  if (normalized.startsWith("FMC")) return "mc";
  if (normalized.startsWith("NMC")) return "nmc";
  return "neutral";
}

function getAircraftStatusColor(status) {
  const tone = getAircraftStatusTone(status);
  if (tone === "mc") return "#16a34a";
  if (tone === "p") return "#facc15";
  if (tone === "nmc") return "#b42318";
  if (tone === "cann") return "#d1d5db";
  return "#64748b";
}

function normalizeSpotLabel(value) {
  return String(value || "").trim().toUpperCase();
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function createAircraftIcon(row, hasOpenIssue) {
  const tone = getAircraftStatusTone(row.status);
  const tailText = String(row.tail || "Tail");
  const tail = escapeHtml(tailText);
  const markerWidth = Math.max(56, Math.min(132, tailText.length * 7 + 18));
  return L.divIcon({
    className: "map-aircraft-marker-shell",
    html: `
      <div class="map-aircraft-marker map-aircraft-marker--${tone}${hasOpenIssue ? " has-open-issue" : ""}">
        <span class="map-aircraft-marker__tail">${tail}</span>
      </div>
    `,
    iconSize: [markerWidth, 28],
    iconAnchor: [markerWidth / 2, 14],
    popupAnchor: [0, -16],
  });
}

function createParkingSpotIcon(spot, isEditMode) {
  return L.divIcon({
    className: "map-parking-spot-marker-shell",
    html: `<div class="map-parking-spot-marker${isEditMode ? " is-editing" : ""}">${escapeHtml(spot.spotLabel)}</div>`,
    iconSize: [58, 24],
    iconAnchor: [29, 12],
    popupAnchor: [0, -14],
  });
}

function ParkingSpotPlacement({ enabled, onPlace }) {
  useMapEvents({
    click(event) {
      if (!enabled) return;
      onPlace(event.latlng);
    },
  });

  return null;
}

export default function MapTab({ api, user, selectedBase, active, baseCoords = {} }) {
  const [peeps, setPeeps] = useState([]);
  const [aircraftRows, setAircraftRows] = useState([]);
  const [issues, setIssues] = useState([]);
  const [parkingSpots, setParkingSpots] = useState([]);
  const [canManageParkingSpots, setCanManageParkingSpots] = useState(false);
  const [layoutEditMode, setLayoutEditMode] = useState(false);
  const [spotDraft, setSpotDraft] = useState("");
  const [mapMessage, setMapMessage] = useState("");
  const [center, setCenter] = useState(baseCoords[selectedBase] || [0, 0]);
  const [satelliteView, setSatelliteView] = useState(false);
  const [aircraftStatusExpanded, setAircraftStatusExpanded] = useState(false);

  useEffect(() => {
    const fetchLocations = () => {
      api
        .get("/get-user-locations", {
          params: {
            email: user.email,
            location: selectedBase
          }
        })
        .then(res => setPeeps(res.data.data || []))
        .catch(console.error);
    };

    fetchLocations();
    window.addEventListener("mfe:user-location-updated", fetchLocations);
    const interval = setInterval(fetchLocations, 1000 * 60 * 2);
    return () => {
      window.removeEventListener("mfe:user-location-updated", fetchLocations);
      clearInterval(interval);
    };
  }, [user, selectedBase, api]);

  useEffect(() => {
    const fetchAircraftContext = () => {
      Promise.all([
        api.get("/get-aircraft-status", {
          params: {
            email: user.email,
            location: selectedBase
          }
        }),
        api.get("/get-issues-log", {
          params: {
            email: user.email,
            location: selectedBase
          }
        }),
        api.get("/get-base-parking-spots", {
          params: {
            email: user.email,
            location: selectedBase
          }
        })
      ])
        .then(([statusRes, issuesRes, parkingRes]) => {
          setAircraftRows(statusRes.data.data || []);
          setIssues(issuesRes.data.data || []);
          setParkingSpots(parkingRes.data.data || []);
          setCanManageParkingSpots(Boolean(parkingRes.data.canManageParkingSpots));
        })
        .catch(console.error);
    };

    fetchAircraftContext();
    const interval = setInterval(fetchAircraftContext, 1000 * 60 * 2);
    return () => clearInterval(interval);
  }, [user, selectedBase, api]);

  useEffect(() => {
    if (!active) return;

    Promise.all([
      api.get("/get-aircraft-status", {
        params: {
          email: user.email,
          location: selectedBase
        }
      }),
      api.get("/get-issues-log", {
        params: {
          email: user.email,
          location: selectedBase
        }
      })
    ])
      .then(([statusRes, issuesRes]) => {
        setAircraftRows(statusRes.data.data || []);
        setIssues(issuesRes.data.data || []);
      })
      .catch(console.error);
  }, [active, user, selectedBase, api]);

  useEffect(() => {
    if (baseCoords[selectedBase]) {
      setCenter(baseCoords[selectedBase]);
    }
  }, [selectedBase, baseCoords]);

  const shopColorMap = useMemo(() => buildShopColorMap(peeps), [peeps]);
  const legendEntries = useMemo(
    () => Object.entries(shopColorMap).sort(([left], [right]) => left.localeCompare(right)),
    [shopColorMap]
  );
  const openIssuesByTail = useMemo(() => issues.reduce((countsByTail, issue) => {
    if (issue.fixed) return countsByTail;
    const tail = String(issue.tail || "").trim().toUpperCase();
    if (!tail) return countsByTail;
    const counts = countsByTail.get(tail) || { open: 0, redBalls: 0 };
    counts.open += 1;
    if (issue.redBallFlag) counts.redBalls += 1;
    countsByTail.set(tail, counts);
    return countsByTail;
  }, new Map()), [issues]);
  const parkingSpotsByLabel = useMemo(() => parkingSpots.reduce((map, spot) => {
    map.set(normalizeSpotLabel(spot.spotLabel), spot);
    return map;
  }, new Map()), [parkingSpots]);
  const mappedAircraft = useMemo(() => aircraftRows
    .map(row => {
      const spot = parkingSpotsByLabel.get(normalizeSpotLabel(row.spot));
      return spot ? { row, spot, coords: [Number(spot.latitude), Number(spot.longitude)] } : null;
    })
    .filter(Boolean), [aircraftRows, parkingSpotsByLabel]);
  const unmappedAircraft = useMemo(() => aircraftRows
    .filter(row => !parkingSpotsByLabel.has(normalizeSpotLabel(row.spot)))
    .slice()
    .sort((left, right) => String(left.tail || "").localeCompare(String(right.tail || ""), undefined, { numeric: true })), [aircraftRows, parkingSpotsByLabel]);

  const refreshParkingSpots = async () => {
    const res = await api.get("/get-base-parking-spots", {
      params: {
        email: user.email,
        location: selectedBase
      }
    });
    setParkingSpots(res.data.data || []);
    setCanManageParkingSpots(Boolean(res.data.canManageParkingSpots));
  };

  const saveParkingSpot = async (spot) => {
    try {
      const res = await api.post("/save-base-parking-spot", {
        email: user.email,
        location: selectedBase,
        id: spot.id,
        spotLabel: spot.spotLabel,
        latitude: spot.latitude,
        longitude: spot.longitude,
        notes: spot.notes || "",
      });
      setMapMessage(res.data.message || "Parking spot saved.");
      setSpotDraft("");
      await refreshParkingSpots();
      setTimeout(() => setMapMessage(""), 2500);
    } catch (err) {
      console.error("Parking spot save failed", err);
      setMapMessage(err.response?.data?.message || "Unable to save parking spot.");
      setTimeout(() => setMapMessage(""), 3500);
    }
  };

  const placeParkingSpot = (latlng) => {
    const label = spotDraft.trim();
    if (!label) {
      setMapMessage("Enter a spot label first, then click the map.");
      setTimeout(() => setMapMessage(""), 2500);
      return;
    }
    saveParkingSpot({
      spotLabel: label,
      latitude: latlng.lat,
      longitude: latlng.lng,
    });
  };

  const deleteParkingSpot = async (spot) => {
    if (!window.confirm(`Delete parking spot ${spot.spotLabel}?`)) return;
    try {
      const res = await api.post("/delete-base-parking-spot", {
        email: user.email,
        location: selectedBase,
        id: spot.id,
      });
      setMapMessage(res.data.message || "Parking spot deleted.");
      await refreshParkingSpots();
      setTimeout(() => setMapMessage(""), 2500);
    } catch (err) {
      console.error("Parking spot delete failed", err);
      setMapMessage(err.response?.data?.message || "Unable to delete parking spot.");
      setTimeout(() => setMapMessage(""), 3500);
    }
  };

  return (
    <div className="map-tab-shell">
      <div className="map-tab-canvas-wrap">
        <MapContainer
          className="map-tab-canvas"
          center={center}
          zoom={16}
          zoomControl={false}
          scrollWheelZoom
        >
        {satelliteView ? (
          <TileLayer
            key="satellite"
            url={SATELLITE_TILE_URL}
            maxNativeZoom={16}
            attribution="Map services and data available from U.S. Geological Survey, National Geospatial Program"
          />
        ) : (
          <TileLayer
            key="streets"
            url={STREET_TILE_URL}
            attribution="&copy; OpenStreetMap contributors"
          />
        )}
        <ZoomControl position="bottomright" />
        <ParkingSpotPlacement enabled={layoutEditMode && canManageParkingSpots} onPlace={placeParkingSpot} />
        <MapRecenter center={center} active={active} />
        {peeps.filter(isFreshUserLocation).map((u) => {
          const shop = u.shop || "Unassigned";
          const markerColor = shopColorMap[shop] || "#64748b";
          const isCurrentUser = u.email === user.email;

          return (
            <CircleMarker
              key={u.email}
              center={[u.lat, u.lng]}
              radius={isCurrentUser ? 9 : 6}
              pathOptions={{
                color: isCurrentUser ? "#0f172a" : markerColor,
                weight: isCurrentUser ? 3 : 2,
                fillColor: markerColor,
                fillOpacity: 0.8
              }}
            >
              <Popup>
                <div>
                  <div><strong>{u.email}</strong></div>
                  <div>Shop: {shop}</div>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
        {layoutEditMode && parkingSpots.map((spot) => (
          <Marker
            key={`spot-${spot.id}`}
            position={[Number(spot.latitude), Number(spot.longitude)]}
            icon={createParkingSpotIcon(spot, true)}
            draggable={canManageParkingSpots}
            eventHandlers={{
              dragend(event) {
                const latlng = event.target.getLatLng();
                saveParkingSpot({ ...spot, latitude: latlng.lat, longitude: latlng.lng });
              }
            }}
          >
            <Popup>
              <div className="map-popup">
                <div><strong>Parking Spot {spot.spotLabel}</strong></div>
                <div>Drag marker to reposition.</div>
                <button type="button" onClick={() => deleteParkingSpot(spot)}>Delete Spot</button>
              </div>
            </Popup>
          </Marker>
        ))}
        {!layoutEditMode && parkingSpots.map((spot) => (
          <Marker
            key={`spot-label-${spot.id}`}
            position={[Number(spot.latitude), Number(spot.longitude)]}
            icon={createParkingSpotIcon(spot, false)}
            zIndexOffset={100}
          />
        ))}
        {!layoutEditMode && mappedAircraft.map(({ row, coords }) => {
          const tail = String(row.tail || "").trim();
          const issueCounts = openIssuesByTail.get(tail.toUpperCase()) || { open: 0, redBalls: 0 };
          const hasOpenIssue = issueCounts.open > 0;

          return (
            <Marker
              key={`aircraft-${row.id || tail}`}
              position={coords}
              icon={createAircraftIcon(row, hasOpenIssue)}
              zIndexOffset={500}
            >
              <Popup>
                <div>
                  <div><strong>Tail {tail || "Unknown"}</strong></div>
                  <div>Status: {row.status || "Unknown"} · Spot: {row.spot || "Unassigned"}</div>
                  <div>
                    Open Issues Log items: {issueCounts.open}
                    {hasOpenIssue ? ` (${issueCounts.redBalls} red ball${issueCounts.redBalls === 1 ? "" : "s"})` : ""}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
        <MapInvalidateOnShow active={active} />
        </MapContainer>
        <button
          type="button"
          className={`map-tab-basemap-toggle${satelliteView ? " is-active" : ""}`}
          onClick={() => setSatelliteView(current => !current)}
          aria-label={satelliteView ? "Switch to street map" : "Switch to satellite view"}
          aria-pressed={satelliteView}
          title={satelliteView ? "Switch to street map" : "Satellite view: USGS aerial imagery, best at U.S. bases"}
        >
          <Satellite size={20} aria-hidden="true" />
        </button>
      </div>

      <div
        className={`map-tab-overlay map-tab-shop-legend${legendEntries.length === 0 ? " is-empty" : ""}`}
      >
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Shop Legend</div>
        <div style={{ display: "grid", gap: 8 }}>
          {legendEntries.length > 0 ? legendEntries.map(([shop, color]) => (
            <div
              key={shop}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: "0.9rem"
              }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: color,
                  border: "1px solid rgba(15, 23, 42, 0.25)",
                  flexShrink: 0
                }}
              />
              <span>{shop}</span>
            </div>
          )) : (
            <div style={{ color: "#5b7186", fontSize: "0.9rem" }}>No user locations available</div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              paddingTop: 6,
              borderTop: "1px solid rgba(23, 48, 71, 0.08)",
              fontSize: "0.85rem",
              color: "#405b73"
            }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: "#cbd5e1",
                border: "3px solid #0f172a",
                flexShrink: 0
              }}
            />
            <span>Current user outline</span>
          </div>
        </div>
      </div>

      <div
        className="map-tab-overlay map-tab-ramp-layout"
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 8 }}>
          <strong>Ramp Layout</strong>
          {canManageParkingSpots ? (
            <button type="button" className="map-layout-button" onClick={() => setLayoutEditMode(current => !current)}>
              {layoutEditMode ? "Done" : "Edit Spots"}
            </button>
          ) : null}
        </div>
        {layoutEditMode ? (
          <div style={{ display: "grid", gap: 8 }}>
            <label className="map-layout-label">
              <span>New spot label</span>
              <input value={spotDraft} onChange={event => setSpotDraft(event.target.value)} placeholder="A01, Spot 11..." />
            </label>
            <div style={{ color: "#536b80", fontSize: "0.82rem", fontWeight: 700 }}>
              Enter a spot label, click the map to place it, then drag saved spots to fine tune.
            </div>
          </div>
        ) : (
          <div style={{ color: "#536b80", fontSize: "0.86rem", fontWeight: 700 }}>
            {parkingSpots.length} saved spot{parkingSpots.length === 1 ? "" : "s"} for {selectedBase}.
          </div>
        )}
        {mapMessage ? <div className="map-layout-message">{mapMessage}</div> : null}
      </div>

      <div
        className={`map-tab-overlay map-tab-aircraft-status${aircraftRows.length === 0 ? " is-empty" : ""}${aircraftStatusExpanded ? "" : " is-collapsed"}`}
      >
        <button
          type="button"
          className="map-tab-aircraft-status__toggle"
          onClick={() => setAircraftStatusExpanded(current => !current)}
          aria-expanded={aircraftStatusExpanded}
          aria-controls="map-aircraft-status-details"
        >
          <span>Aircraft Status</span>
          <span className="map-tab-aircraft-status__count">{aircraftRows.length} aircraft</span>
          <ChevronDown size={18} aria-hidden="true" />
        </button>
        <div id="map-aircraft-status-details" hidden={!aircraftStatusExpanded}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10, fontSize: "0.78rem" }}>
          <span><span style={{ ...legendDotStyle, background: "#16a34a" }} />FMC</span>
          <span><span style={{ ...legendDotStyle, background: "#facc15" }} />P*</span>
          <span><span style={{ ...legendDotStyle, background: "#b42318" }} />NMC</span>
          <span><span style={{ ...legendDotStyle, background: "#d1d5db" }} />CANN</span>
          <span><span style={{ ...legendDotStyle, background: "#64748b" }} />Unknown</span>
          <span><span style={{ ...legendDotStyle, background: "#fff", border: "3px solid #f97316" }} />Open Issue Log Item</span>
        </div>
        {aircraftRows.length > 0 ? (
          <div style={{ display: "grid", gap: 6 }}>
            {unmappedAircraft.map(row => {
              const tail = String(row.tail || "").trim();
              const hasOpenIssue = (openIssuesByTail.get(tail.toUpperCase())?.open || 0) > 0;
              return (
                <div
                  key={row.id || tail}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "18px minmax(0, 1fr) auto",
                    gap: 8,
                    alignItems: "center",
                    fontSize: "0.86rem"
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: getAircraftStatusColor(row.status),
                      border: hasOpenIssue ? "3px solid #f97316" : "1px solid rgba(15, 23, 42, 0.28)"
                    }}
                  />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <strong>{tail || "Unknown tail"}</strong> {row.spot ? `Spot ${row.spot}` : "No spot"}
                  </span>
                  <span style={{ color: hasOpenIssue ? "#b45309" : "#536b80", fontWeight: 800 }}>
                    {hasOpenIssue ? "Open" : row.status || "--"}
                  </span>
                </div>
              );
            })}
            {mappedAircraft.length > 0 ? (
              <div style={{ color: "#536b80", fontSize: "0.82rem", marginTop: 4 }}>
                {mappedAircraft.length} aircraft plotted on saved spot coordinates.
              </div>
            ) : (
              <div style={{ color: "#536b80", fontSize: "0.82rem", marginTop: 4 }}>
                Add saved ramp spots, then make Status spot values match those labels to plot aircraft.
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: "#5b7186", fontSize: "0.9rem" }}>No aircraft status rows available.</div>
        )}
        </div>
      </div>
    </div>
  );
}

const legendDotStyle = {
  display: "inline-block",
  width: 10,
  height: 10,
  borderRadius: "50%",
  marginRight: 5,
  verticalAlign: "-1px",
  border: "1px solid rgba(15, 23, 42, 0.25)"
};
